import FlatCard from 'components/common/flatCard/FlatCard'
import IconRender from 'components/common/IconRender'
import Slide from 'components/common/Slide'
import { formatDate } from 'components/utils/FormatDate'
import { Unit } from 'newTypes/unit'
import { IconSizes } from 'types/general'

const UnitCard = ({ unit }: { unit: Unit }) => {
  const fullAddress = [
    unit.address.street,
    unit.address.number && `No. ${unit.address.number}`,
    unit.address.doorNumber && `Apt ${unit.address.doorNumber}`,
    unit.address.city,
    unit.address.province,
  ]
    .filter(Boolean)
    .join(', ')

  return (
    <FlatCard customStyle="flex flex-wrap gap-6 p-6 max-w-5xl mx-auto">
      <div className="min-w-[28rem] w-full sm:w-auto flex-1">
        <Slide images={unit.images} hiddePreviews />
      </div>

      <div className="flex-1 min-w-[18rem] flex flex-col gap-2 text-sm text-gray-600">
        <div className="flex gap-2">
          {unit.verified && (
            <span className="bg-green-100 text-green-600 text-xs font-medium px-2 py-1 rounded">
              Ownership Verified
            </span>
          )}
        </div>
        {unit.title ? (
          <h2 className="text-xl font-semibold text-blue-700">{unit.title}</h2>
        ) : (
          <h2 className="text-xl font-semibold text-blue-700">
            Cozy {unit.bedrooms} bedrooms property
          </h2>
        )}
        <div className="w-full flex justify-between items-center">
          <div className="flex items-center gap-2">
            {unit.details.heat && (
              <IconRender icon="regular-temperature-high" size={IconSizes.LG} />
            )}
            {unit.details.electricity && (
              <IconRender icon="regular-plug" size={IconSizes.LG} />
            )}
            {unit.details.internet && (
              <IconRender icon="regular-wifi" size={IconSizes.LG} />
            )}
            {unit.details.parking && (
              <IconRender icon="regular-circle-parking" size={IconSizes.LG} />
            )}
            {unit.details.petFriendly && (
              <IconRender icon="regular-paw" size={IconSizes.LG} />
            )}
            {unit.details.snowRemoval && (
              <IconRender icon="regular-snowflake" size={IconSizes.LG} />
            )}
            {unit.details.furnished && (
              <IconRender icon="regular-refrigerator" size={IconSizes.LG} />
            )}
            {unit.details.semiFurnished && !unit.details.furnished && (
              <IconRender icon="regular-oven" size={IconSizes.LG} />
            )}
          </div>
          <p className="flex items-center text-2xl font-bold text-gray-700">
            ${unit.price}
            <span className="text-base font-normal">/month</span>
          </p>
        </div>

        <div className="flex items-center gap-2 mt-4">
          <IconRender icon="regular-location-dot" size={IconSizes.MD} />
          {fullAddress}
        </div>

        <div className="flex flex-wrap gap-x-6 gap-y-2 mt-4">
          <div className="flex items-center gap-2">
            <IconRender icon="regular-bed" size={IconSizes.MD} />
            {unit.bedrooms} Beds
          </div>
          <div className="flex items-center gap-2">
            <IconRender icon="regular-bath" size={IconSizes.MD} />
            {unit.bathrooms} Bath
          </div>
          {unit.size && (
            <div className="flex items-center gap-2">
              <IconRender icon="regular-expand" size={IconSizes.MD} />
              {unit.size} {unit.sizeUnit}
            </div>
          )}
        </div>
        <div className="w-full flex justify-between items-center gap-2 mt-4">
          {unit.availability && (
            <div className="flex items-center gap-2">
              <IconRender icon="regular-calendar-week" size={IconSizes.MD} />
              Available: {formatDate(unit.availability)}
            </div>
          )}
          {unit.contractEnd && (
            <div className="flex items-center gap-2">
              <IconRender icon="regular-calendar-xmark" size={IconSizes.MD} />
              Ends on: {formatDate(unit.contractEnd)}
            </div>
          )}
        </div>

        <div className="mt-4">
          <div className="flex items-center gap-2">
            <IconRender icon="regular-clock" size={IconSizes.SM} />
            Listed on: {formatDate(unit.createdAt)}
          </div>
        </div>
      </div>
    </FlatCard>
  )
}

export default UnitCard
